<script setup>
import {plainStripeError} from '~/utils/stripeErrors.js';
// Save-a-card dialog (mockup account-payments.html "Add card" — the Stripe
// Payment Element body, wallets first). Flow:
//   POST api/v2/paymentMethods/setupIntent → { clientSecret }
//   → mount the Stripe Payment Element → stripe.confirmSetup
//   → emits 'saved' so the parent can re-fetch paymentMethods/mine.
import TlDialog from "~/components/TlDialog.vue";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {useStripePayments} from "~/composables/useStripePayments";
import {usePaymentsDisplay} from "~/composables/usePaymentsDisplay";
import {endpoints} from "~/network/endpoints.js";
import {nextTick, ref, watch} from "vue";
import {SAVE_CARD_AUTHORISATION} from '~/composables/usePayingHonestly.js';

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  title: {
    type: String,
    default: 'Add card'
  }
});
const emit = defineEmits(['update:modelValue', 'saved']);

const {httpRequest} = useHttpRequest();
const {mountPaymentElement, confirmSetup} = useStripePayments();
const {normalizeErrors} = usePaymentsDisplay();

const elementHost = ref(null);
const setupPending = ref(false);
const savePending = ref(false);
const errors = ref({});
const stripeError = ref(null);
const elementReady = ref(false);
let session = null; // {stripe, elements, paymentElement}

const teardown = () => {
  try {
    session?.paymentElement?.destroy();
  } catch {
    // already gone
  }
  session = null;
  elementReady.value = false;
};

const initialise = async () => {
  setupPending.value = true;
  errors.value = {};
  stripeError.value = null;
  try {
    const res = await httpRequest(endpoints.payments.setupIntent, {});
    await nextTick();
    if (!elementHost.value) return;
    session = await mountPaymentElement(res.clientSecret, elementHost.value, {savingCard: true});
    session.paymentElement.on('ready', () => {
      elementReady.value = true;
    });
  } catch (e) {
    errors.value = normalizeErrors(e, 'Could not start the card setup. Please try again');
  } finally {
    setupPending.value = false;
  }
};

watch(() => props.modelValue, (open) => {
  if (open) {
    initialise();
  } else {
    teardown();
  }
});

const save = async () => {
  if (!session) return;
  savePending.value = true;
  stripeError.value = null;
  try {
    const {error} = await confirmSetup(session);
    if (error) {
      // Stripe's message verbatim (card declined / incomplete details / …)
      stripeError.value = plainStripeError(error);
      return;
    }
    emit('saved');
    emit('update:modelValue', false);
  } finally {
    savePending.value = false;
  }
};
</script>

<template>
  <tl-dialog
      :model-value="modelValue"
      :title="title"
      :max-width="440"
      @update:model-value="emit('update:modelValue', $event)"
  >
    <p class="text-body-2 text-medium-emphasis mb-3">
      Your card is saved securely with Stripe. Clubs and Tap Leagues never see the number.
      Apple&nbsp;Pay / Google&nbsp;Pay save the same way.
    </p>

    <div v-if="setupPending" class="pe-loading">
      <v-progress-circular indeterminate size="22" width="2" color="primary"/>
      <span>Preparing secure card form…</span>
    </div>

    <div ref="elementHost"/>

    <v-alert
        v-if="stripeError"
        type="error"
        variant="tonal"
        density="compact"
        class="mt-3"
    >{{ stripeError }}</v-alert>
    <crud-error-display class="mt-3" :errors="errors"/>
    <!-- PSRs 2017 reg 67: what saving the card authorises, above the save button,
         replacing Stripe's own mandate line (terms: {card: 'never'}). -->
    <p class="text-body-2 mt-3 mb-0">{{ SAVE_CARD_AUTHORISATION }}</p>

    <template #actions>
      <v-btn variant="outlined" @click="emit('update:modelValue', false)">Cancel</v-btn>
      <v-btn
          color="primary"
          prepend-icon="mdi-credit-card-plus-outline"
          :disabled="!elementReady"
          :loading="savePending"
          @click="save"
      >Save card</v-btn>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.pe-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 22px 14px;
  border: 1.5px dashed rgb(var(--v-theme-borderLight));
  border-radius: 10px;
  background: rgb(var(--v-theme-light));
  color: rgb(var(--v-theme-textSubtle));
  font-size: 12px;
  font-weight: 600;
}
</style>
